import { useEffect, useState } from 'react';
import { useApiErrorHandler } from '@/lib/useApiErrorHandler';
import { useRoundsData } from './useRoundsData';
import { useRoundDerived } from './useRoundDerived';
import { useRoundResults } from './useRoundResults';
import { useRoundLifecycle } from './useRoundLifecycle';
import { useRoundGeneration } from './useRoundGeneration';
import { useTableReorderAndSwap } from './useTableReorderAndSwap';

export function useRoundsController(
  tournamentId: string | undefined,
  // Story 083 — God Mode is now owned hub-wide (the header toggle),
  // passed in via the outlet context. The controller no longer owns it.
  godMode: boolean,
  // Story 093 — the viewer's role; co-directors don't get owner-only
  // round-lifecycle controls (publish / close round).
  isOwner: boolean,
) {
  const data = useRoundsData(tournamentId);
  const [editMode, setEditMode] = useState(false);
  const [noValidPairing, setNoValidPairing] = useState(false);

  // Reset manual-edit mode when navigating to a different tournament.
  useEffect(() => {
    setEditMode(false);
  }, [tournamentId]);

  // Action errors: show toast; on network error, resync from server
  // (backend may have processed the request but the response was lost)
  const handleActionError = useApiErrorHandler(data.loadData);

  // Computed early because both derived and generation need it
  const isKo = data.tournament?.system === '3ko' || data.tournament?.system === '5ko';

  const generation = useRoundGeneration({
    tournamentId,
    rounds: data.rounds,
    setRounds: data.setRounds,
    setTournament: data.setTournament,
    selectedRound: data.selectedRound,
    setSelectedRound: data.setSelectedRound,
    isKo,
    godMode,
    noValidPairing,
    setNoValidPairing,
    handleActionError,
  });

  const lifecycle = useRoundLifecycle({
    tournamentId,
    tournament: data.tournament,
    setTournament: data.setTournament,
    rounds: data.rounds,
    setRounds: data.setRounds,
    selectedRound: data.selectedRound,
    setSelectedRound: data.setSelectedRound,
    setShowFallbackNotice: generation.setShowFallbackNotice,
    handleActionError,
  });

  const results = useRoundResults({
    tournamentId,
    rounds: data.rounds,
    setRounds: data.setRounds,
    handleActionError,
  });

  const reorder = useTableReorderAndSwap({
    tournamentId,
    rounds: data.rounds,
    setRounds: data.setRounds,
    selectedRound: data.selectedRound,
    handleActionError,
  });

  const derived = useRoundDerived({
    tournament: data.tournament,
    rounds: data.rounds,
    selectedRound: data.selectedRound,
    players: data.players,
    godMode,
    noValidPairing,
  });

  // Story 083 — when the hub-wide God Mode turns off, drop every
  // manual-edit sub-state so no stale edit/swap/add-pairing UI is left
  // behind. (Was handled inline by the page-level toggle before the
  // toggle moved to the hub header.)
  useEffect(() => {
    if (!godMode) {
      setEditMode(false);
      reorder.setSwapSelection(null);
      generation.setShowAddPairing(false);
    }
    // Only react to godMode flips. reorder/generation expose stable
    // useState setters; including the objects would re-fire every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [godMode]);

  return {
    // state
    tournament: data.tournament,
    rounds: data.rounds,
    loading: data.loading,
    generating: generation.generating,
    dragIndex: reorder.dragIndex,
    selectedRound: data.selectedRound,
    setSelectedRound: data.setSelectedRound,
    pendingAction: results.pendingAction,
    swapSelection: reorder.swapSelection,
    setSwapSelection: reorder.setSwapSelection,
    players: data.players,
    showAddPairing: generation.showAddPairing,
    setShowAddPairing: generation.setShowAddPairing,
    godMode,
    isOwner,
    isChess: data.tournament?.game === 'chess',
    editMode,
    setEditMode,
    showCompletionPrompt: lifecycle.showCompletionPrompt,
    setShowCompletionPrompt: lifecycle.setShowCompletionPrompt,
    showDeletePrompt: lifecycle.showDeletePrompt,
    setShowDeletePrompt: lifecycle.setShowDeletePrompt,
    showFallbackNotice: generation.showFallbackNotice,
    setShowFallbackNotice: generation.setShowFallbackNotice,
    // handlers
    handleGenerate: generation.handleGenerate,
    handleGenerateAllRounds: generation.handleGenerateAllRounds,
    handlePublish: lifecycle.handlePublish,
    handleSetRoundDate: lifecycle.handleSetRoundDate,
    handleSetResult: results.handleSetResult,
    handleConfirmResult: results.handleConfirmResult,
    handleCompleteRound: lifecycle.handleCompleteRound,
    handleCompleteTournament: lifecycle.handleCompleteTournament,
    handleUncompleteTournament: lifecycle.handleUncompleteTournament,
    handleDragStart: reorder.handleDragStart,
    handleDragEnd: reorder.handleDragEnd,
    handleDragOver: reorder.handleDragOver,
    handleDrop: reorder.handleDrop,
    handleSwap: reorder.handleSwap,
    handleVoidResult: results.handleVoidResult,
    handleToggleInProgress: results.handleToggleInProgress,
    handleUnpublish: lifecycle.handleUnpublish,
    handleCreateManualRound: generation.handleCreateManualRound,
    handleUncomplete: lifecycle.handleUncomplete,
    handleDeleteRound: lifecycle.handleDeleteRound,
    handleAddManualPairing: generation.handleAddManualPairing,
    handlePlayerSelect: reorder.handlePlayerSelect,
    // derived
    currentRound: derived.currentRound,
    isRoundRobin: derived.isRoundRobin,
    canGenerate: derived.canGenerate,
    hasIncompleteRounds: derived.hasIncompleteRounds,
    canUnpublish: derived.canUnpublish,
    allResultsConfirmed: derived.allResultsConfirmed,
    isKo: derived.isKo,
    allRoundsCompleted: derived.allRoundsCompleted,
    activePlayerCount: derived.activePlayerCount,
    allPlannedRoundsDone: derived.allPlannedRoundsDone,
    isOpenEnded: derived.isOpenEnded,
    canCompleteTournament: derived.canCompleteTournament,
    gameConfig: derived.gameConfig,
    resultOptions: derived.resultOptions,
  };
}

export type RoundsController = ReturnType<typeof useRoundsController>;
